import { useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { queryKeys } from '@/lib/data/keys';
import {
  getScores365,
  getSportsEvents,
  type Scores365SportId,
  type SportId,
  type SportsMode,
} from '@/lib/data/provider';

/**
 * The Esports & Sports schedule for one discipline + slice (Bzzoiro Sports
 * Addon). Live slices poll every 30s (Realtime doesn't cover this addon);
 * upcoming/finished use the calmer fixtures cadence. Refetches on focus so a
 * returning user always sees a fresh board.
 */
export function useSportsEvents(sport: SportId, mode: SportsMode, enabled = true) {
  return useQuery({
    queryKey: queryKeys.sportsEvents(sport, mode),
    queryFn: () => getSportsEvents(sport, mode),
    staleTime: mode === 'live' ? QUERY_STALE_TIME.live : QUERY_STALE_TIME.fixtures,
    refetchInterval: mode === 'live' ? QUERY_STALE_TIME.live : false,
    enabled: enabled && isSupabaseConfigured,
  });
}

/**
 * The same schedule slice for one of the six 365scores disciplines (Basketball,
 * American Football, Baseball, Volleyball, Handball, Rugby) via the lazy
 * `scores-365` edge function. The screen drives these with a Yesterday/Today/
 * Tomorrow date strip, so `date` (YYYY-MM-DD) selects an explicit calendar day;
 * omit it for the legacy today view. `mode` still tags the request bucket
 * (live / upcoming / finished). Live polls every 30s only when actually viewing
 * the live slice of TODAY — a past or future day is static, so it uses the
 * calmer fixtures cadence and never polls.
 */
export function useScores365(
  sportId: Scores365SportId,
  mode: SportsMode,
  enabled = true,
  date?: string,
) {
  // Only the live slice of today is a moving target worth polling.
  const isLiveNow = mode === 'live' && !date;
  return useQuery({
    queryKey: queryKeys.scores365(sportId, mode, date),
    queryFn: () => getScores365(sportId, mode, date),
    staleTime: isLiveNow ? QUERY_STALE_TIME.live : QUERY_STALE_TIME.fixtures,
    refetchInterval: isLiveNow ? QUERY_STALE_TIME.live : false,
    enabled: enabled && isSupabaseConfigured,
  });
}
